import { CLAIMS_DOMAIN, useAuthentication } from "@providers/AuthenticationProvider";
import { ConfigurationItem } from "@sourceful/shared-types";
import { updateConfigItemRecord } from "../_api";
import GuestConfigItemManager from "@lib/GuestConfigItemManager/GuestConfigItemManager";
import { configItemToUpdatePayload } from "../helpers/format-helpers";
import { useCallback } from "react";
import { isGuestConfigItem } from "@sourceful/shared-utils/configuration-utils";
interface _SyncConfigItemOptions {
  syncGuestItem?: boolean;
  userId: string | null;
  userUuidInternal: string | null;
  orgId: string | null;
  token: string | null;
  onUpdate?: (item: ConfigurationItem) => void;
}

/*
    used for persisting changes to configuration items - either in the database (for authenticated users) or temporarily in local storage 
*/
const _syncConfigItem = async (
  item: ConfigurationItem,
  { syncGuestItem, orgId, userId, userUuidInternal, token, onUpdate }: _SyncConfigItemOptions
) => {
  // Guest configs are not saved
  // Check user or item id, G- indicates guest
  if (!userUuidInternal || !userId || isGuestConfigItem(item.id.toString())) {
    if (userId) {
      // We have an issue, as we have a user which is logged in but has a guest item
      // We'll log these for now and just carry on
      console.error(
        `User has guest config item - this should not happen, user: ${userId}, item: ${item.id}`
      );
    }
    if (userUuidInternal) {
      // We have an issue, as we have a user which is logged in but has a guest item
      // We'll log these for now and just carry on
      console.error(
        `User has guest config item - this should not happen, user: ${userUuidInternal}, item: ${item.id}`
      );
    }

    if (syncGuestItem) {
      // we only want to sync guest items to local storage in certain circumstances (i.e. when creating the item and transitioning to the configurator)
      // we don't want to persist guest items between refreshes as it causes complexities with migrating product versions -
      // essentially creates loads of decentralised unstructured databases for us to handle
      const guestItemManager = new GuestConfigItemManager();
      return guestItemManager.setItem(item);
    }

    return;
  }

  if (!orgId) {
    throw new Error("Failed to sync config item - missing org id");
  }

  if (!token) {
    throw new Error("Failed to sync config item - missing token");
  }

  const res = await updateConfigItemRecord({
    token,
    payload: configItemToUpdatePayload(item),
    basketId: item?.basketId,
  });

  onUpdate && onUpdate(item);
  return res;
};

/*
    A convenience hook that does the setup for a lot of the args for the syncConfigItem function
    Also handles side effects post-sync
*/
export type SyncConfigItemOptions = Omit<
  _SyncConfigItemOptions,
  "token" | "userUuidInternal" | "userId" | "orgId"
>;

export interface UseSyncConfigItemProps {
  onItemSynced?: (item: ConfigurationItem) => void;
}

export const useSyncConfigItem = (props?: UseSyncConfigItemProps) => {
  const onItemSynced = props?.onItemSynced;
  const { getAccessTokenSilently, user, organisation } = useAuthentication();

  const onUpdate = useCallback(
    (item: ConfigurationItem) => {
      onItemSynced && onItemSynced(item);
    },
    [onItemSynced]
  );

  // old auth0 user id
  const userId = user?.sub || "";
  // internal user uuid
  const userUuidInternal = user ? user[CLAIMS_DOMAIN].user.uuid : "";

  const syncConfigItem = useCallback(
    async (item: ConfigurationItem, opts?: SyncConfigItemOptions) => {
      const token = await getAccessTokenSilently().catch(() => null);
      _syncConfigItem(
        {
          ...item,
          userId: userId,
          userUuidInternal: userUuidInternal,
          orgId: organisation?.id || "",
        },
        {
          ...opts,
          token,
          userId: userId,
          userUuidInternal: userUuidInternal,
          onUpdate,
          orgId: organisation?.id || null,
        }
      );
    },
    [getAccessTokenSilently, onUpdate, user?.sub, organisation?.id]
  );

  return syncConfigItem;
};
